import {useRef, useState} from 'react'
import {useOnEvent} from 'react-app-events'
import {MdDashboardCustomize} from 'react-icons/md'
import AutoForm from '@components/AutoForm'
import ObjectField from '@components/fields/ObjectField'
import Button from '@components/V3/Button'
import Divider from '@components/V3/Divider'
import Modal from '@components/V3/Modal'
import useScreenSize from '@hooks/useScreenSize'
import useWebsiteId from '@hooks/useWebsiteId'
import Fields from '@packages/orionjs-react-autoform/lib/Fields'
import RequiredPill from '@page-components/CheckoutV3/components/RequiredPill'
import Section from '@page-components/CheckoutV3/components/Section'
import useCheckoutMutation from '@page-components/CheckoutV3/hooks/useCheckoutMutation'
import useModal from '@page-components/CheckoutV3/hooks/useModal'
import {useApolloQuery as useQuery} from 'apollo-hooks'
import gql from 'graphql-tag'
import {useTranslation} from 'next-i18next'
import {Field, Form} from 'simple-react-form'

import FormStateSummary from './FormStateSummary'

export default function CustomFieldsModal() {
  const {closeModal, isOpen, openModal} = useModal('customFields')
  const buttonRef = useRef(null)

  const {t} = useTranslation('checkoutv3', {keyPrefix: 'customFields'})
  const {t: tGeneric} = useTranslation('generic')
  const screenSize = useScreenSize()

  const websiteId = useWebsiteId()

  const {submit, loading} = useCheckoutMutation(
    gql`
      mutation CheckoutV3SetOrderParams($orderParams: JSON, $websiteId: ID!) {
        setUserPreferences(orderParams: $orderParams, websiteId: $websiteId) {
          _id
          orderParams
        }
      }
    `,
    {
      onSuccess: closeModal
    }
  )

  const {preferences} = useQuery({
    query: gql`
      query CheckoutV3GetOrderParams($websiteId: ID!) {
        preferences: userPreferences(websiteId: $websiteId) {
          _id
          orderParams
          website {
            _id
            menuConfiguration {
              orderParamsSchema
            }
          }
        }
      }
    `,
    variables: {
      websiteId
    },
    fetchPolicy: 'cache-first'
  })

  const [formState, setFormState] = useState<{orderParams?: object}>({
    orderParams: preferences.orderParams
  })

  const [errors, setErrors] = useState({})

  useOnEvent('orderParamsError', errors => {
    console.log('orderParamsError', errors)
    const errorMessage = {}
    Object.keys(errors).map(key => {
      const errorCode = errors[key]
      errorMessage[key] = t(`errors.${errorCode}`, {label: 'Este campo'})
    })
    setErrors(errorMessage)
    openModal()
  })

  const onSubmit = () => {
    submit({variables: {websiteId, orderParams: formState.orderParams}})
  }

  const {orderParamsSchema} = preferences?.website?.menuConfiguration || {}

  const requiredFields = Object.keys(orderParamsSchema ?? {})?.filter(
    (key: any) => orderParamsSchema[key].optional === false
  )
  const pendingRequiredFields = requiredFields?.filter(
    (key: any) => !preferences.orderParams?.[key] || preferences.orderParams?.[key] === ''
  )

  return (
    <>
      <Section
        id="customFields"
        caretDirection={isOpen ? 'up' : 'down'}
        compact
        pillText={tGeneric('fields.select')}
        icon={<MdDashboardCustomize size={21} />}
        subsection={
          <FormStateSummary preferences={preferences} orderParamsSchema={orderParamsSchema} />
        }
        onClick={openModal}>
        <div className="flex items-center gap-2">
          <p>{t('title')}</p>
          {pendingRequiredFields?.length > 0 ? <RequiredPill /> : null}
        </div>
      </Section>
      {['mobile', 'sm'].includes(screenSize) ? <Divider /> : null}
      <Modal
        forceJustoFont
        isOpen={isOpen}
        onClose={closeModal}
        header={t('title')}
        initialFocusRef={buttonRef}
        footer={
          <div className="p-2">
            <Button
              loading={loading}
              disabled={loading}
              full
              big
              primary
              className="mt-4"
              onClick={onSubmit}
              ref={buttonRef}>
              {tGeneric('fields.save')}
            </Button>
          </div>
        }>
        <div className="p-2 min-h-[200px]">
          <Form
            onChange={setFormState}
            errorMessages={errors}
            state={formState}
            onSubmit={() => buttonRef.current.click()}>
            <Field fieldName="orderParams" type={ObjectField}>
              <Fields getField={AutoForm.getFieldComponent} params={orderParamsSchema} />
            </Field>
          </Form>
        </div>
      </Modal>
    </>
  )
}
